// Frontend mirrors of the BE DTOs from DASH-2663 (Web.Api.Core/Dto/LabImport/LabImportDtos.cs).
// The API serializes/deserializes these camelCase. Enums are emitted as integers.

// Mirrors Web.Api.Core/Domain/Enums/UnitsEnum.cs — MappedUnit is serialized as this integer (or null).
export enum LabImportUnitsEnum {
  UgPerM3 = 0,
  MgPerM3 = 1,
  Ppb = 2,
  Ppm = 3,
  Db = 4,
  UgPerCm2 = 5,
  UgPerFt2 = 6,
  FPerCc = 7,
  MgPerSample = 8,
  UgPerSample = 9,
  UgPerDl = 10,
  UmolPerL = 11,
  UgPerGCreatinine = 12,
  UgPerL = 13,
  UmolPerMol = 14,
  MPerS2 = 15,
  MPerS175 = 16,
}

// Human-readable labels for the unit override dropdown.
export const LAB_IMPORT_UNIT_LABELS: Record<LabImportUnitsEnum, string> = {
  [LabImportUnitsEnum.UgPerM3]: 'µg/m³',
  [LabImportUnitsEnum.MgPerM3]: 'mg/m³',
  [LabImportUnitsEnum.Ppb]: 'ppb',
  [LabImportUnitsEnum.Ppm]: 'ppm',
  [LabImportUnitsEnum.Db]: 'dB',
  [LabImportUnitsEnum.UgPerCm2]: 'µg/cm²',
  [LabImportUnitsEnum.UgPerFt2]: 'µg/ft²',
  [LabImportUnitsEnum.FPerCc]: 'f/cc',
  [LabImportUnitsEnum.MgPerSample]: 'mg/sample',
  [LabImportUnitsEnum.UgPerSample]: 'µg/sample',
  [LabImportUnitsEnum.UgPerDl]: 'µg/dL',
  [LabImportUnitsEnum.UmolPerL]: 'µmol/L',
  [LabImportUnitsEnum.UgPerGCreatinine]: 'µg/g creatinine',
  [LabImportUnitsEnum.UgPerL]: 'µg/L',
  [LabImportUnitsEnum.UmolPerMol]: 'µmol/mol',
  [LabImportUnitsEnum.MPerS2]: 'm/s²',
  [LabImportUnitsEnum.MPerS175]: 'm/s^1.75',
};

// Detected sheet orientation (DASH-2694 / BE DASH-2693 §3). 'columnar' = analytes in columns / one row
// per sample; 'transposed' = axes swapped; 'headerBlock' = a header/title block precedes the data grid;
// 'ambiguous' = the detector could not decide (always surfaced for confirmation).
export type LabImportOrientation = 'columnar' | 'transposed' | 'headerBlock' | 'ambiguous';

// The BE's nested orientation-detection result (DASH-2693 §3, Web.Api.Core/Dto/LabImport LabImportOrientation),
// serialized camelCase. `requiresConfirmation` is the BE's authoritative "the user must confirm / override
// before commit" flag (a non-columnar / ambiguous layout that was NOT auto-applied); the FE trusts it rather
// than recomputing a client-side confidence gate. `autoApplied` is true when the BE already normalised the
// grid to columnar before mapping.
//
// DASH-2728 CONTRACT CORRECTION — `dataStartRowIndex` on this RESPONSE is the 0-based index of the first
// DATA row the BE built from (i.e. header + 1, and higher when interstitial metadata sits between the header
// and the data), and is 0 on the LOW-confidence path (the BE discards its structural indices and builds at
// header-0 — DASH-2727). It is therefore NOT the same basis as the REQUEST field (see
// LabImportOrientationOverride) and must not be round-tripped back unchanged. `confidence` is the ORIENTATION
// confidence only — independent of the per-analyte / per-unit column-map confidence.
export interface LabImportOrientationInfo {
  detectedOrientation: LabImportOrientation;
  confidence: number;
  dataStartRowIndex: number;
  // The AI's PROPOSED header-row index, 0-based against the RAW grid — the SAME basis the request/override
  // `dataStartRowIndex` uses (see LabImportOrientationOverride), NOT the response's first-data-row
  // `dataStartRowIndex` above. Surfaced on every build path INCLUDING when the proposal is not auto-applied
  // (the low-confidence path, where the response `dataStartRowIndex` is a 0 placeholder), so the FE can
  // prefill the header-row control to the AI's proposal instead of row 1. Optional for deploy-order safety:
  // a BE predating the field omits it, and the FE falls back to the response first-data-row index.
  headerRowIndex?: number;
  autoApplied: boolean;
  requiresConfirmation: boolean;
  // DASH-3159: the APPLIED structure the BE actually built the previewed table from — distinct from what it
  // merely detected/proposed. `transposed` is the transpose flag that was applied (false on the low-confidence
  // path, which builds header-0 without transposing) — it matches the BE's `LabImportOrientation.Transposed`
  // JSON key (NOT `appliedTransposed`, which was never sent). `footerRowIndices` are the 0-based RAW-FILE
  // indices of the rows the BE excluded as footers (empty on the low-confidence path, which discards its
  // structural indices). Echoed back on EVERY commit so the BE rebuilds the identical table deterministically.
  // Optional for deploy-order safety: a BE predating the paired change omits them and the FE falls back to the
  // prior detected-only behaviour.
  transposed?: boolean;
  footerRowIndices?: number[];
}

// User orientation override sent back to analyze() (and echoed on commit) to force a deterministic layout.
// Optional fields: only the ones the user actually set are appended to the multipart request; a BE that
// predates the override binding simply ignores them. `dataStartRowIndex` is the 0-based RAW-FILE index of the
// real HEADER row, matching LabImportAnalyzeRequestModel.DataStartRowIndex / LabImportCommitRequestModel
// .DataStartRowIndex (the BE builds data from headerIndex + 1). This is NOT the response's first-data-row
// value — the FE derives it from the user-facing 1-based header row so "row N" means the row in the user's
// own file, not a post-parse offset (DASH-2728).
export interface LabImportOrientationOverride {
  transposed?: boolean;
  dataStartRowIndex?: number;
}

export interface LabImportColumnMap {
  sampleNumberColumn: string;
  contaminantColumn: string;
  valueColumn: string;
  unitsColumn: string;
  censorTypeColumn: string;
  totalColumn: string;
  totalUnitsColumn: string;
  totalCensorTypeColumn: string;
  methodColumn: string;
  mediaColumn: string;
  sampleDateColumn: string;
  dateReceivedColumn: string;
  labSampleNumberColumn: string;
  fusedColumns: string[];
}

export interface LabImportAnalyteMapping {
  // DASH-2713: the sample this mapping applies to, stamped at commit from the owning LabImportAnalytePreview.
  // The BE keys the commit mapping by (sampleNumber, sourceLabel) so the same analyte label can resolve to a
  // different agent on different samples (per-sample override). Optional because the analyze proposal carries
  // it on the parent preview, not on each analyte — it is only populated onto the flattened commit payload.
  sampleNumber?: string;
  sourceLabel: string;
  mappedAgentId: string;
  mappedAgentName: string;
  confidence: number;
  basis: string; // "cas" | "name" | "ai"
  // Pre-computed in the component after analyze() so the template never calls a method (see components.md).
  lowConfidence?: boolean;
}

export interface LabImportUnitMapping {
  sourceUnit: string;
  mappedUnit: LabImportUnitsEnum | null;
  confidence: number;
  basis: string; // "name" | "ai"
  // Pre-computed in the component after analyze() so the template never calls a method (see components.md).
  lowConfidence?: boolean;
}

// An agent the sample was sampled for (part of its full expected-agent set). agentId is the
// MasterAgentId, matching LabImportAnalyteMapping.mappedAgentId.
export interface LabImportSampleAgent {
  agentId: string;
  name: string;
}

export interface LabImportAnalytePreview {
  sampleNumber: string;
  analytes: LabImportAnalyteMapping[];
  // The sample's FULL expected-agent set (every agent it was sampled for), regardless of whether the
  // AI proposed a mapping to it. Populates the per-sample override dropdown so a reviewer can assign
  // an agent the AI never proposed — including the all-unmapped case.
  // Optional for deploy-order safety: this FE may reach an environment before the BE that supplies it
  // has deployed (separate repos/pipelines, non-atomic deploy). When absent, buildAgentOptions() falls
  // back to the analyte-derived agents, so the dropdown degrades to the prior behaviour instead of empty.
  expectedAgents?: LabImportSampleAgent[];
}

export interface LabImportSampleMatchError {
  sampleNumber: string;
  reason: string;
}

export interface LabImportProposal {
  columns: LabImportColumnMap;
  samples: LabImportAnalytePreview[];
  units: LabImportUnitMapping[];
  sampleMatchErrors: LabImportSampleMatchError[];
  headers: string[];
  preview: string[][];
  applyBlankCorrection: boolean;
  detectedBlankCount: number;
  warnings: string[];
  // AC 13 number-format / dialect. The BE proposal does not yet surface these; the FE detects them
  // client-side from the CSV. Kept optional for forward-compat: if the BE later returns them, they
  // take precedence over the client-side detection in the read-only dialect banner.
  delimiter?: string;
  decimalSeparator?: string;
  // Orientation detection (DASH-2694 / BE DASH-2693 §3). A nested object, serialized camelCase. Optional for
  // deploy-order safety: a BE predating §3 omits `orientation` entirely, so the override panel stays hidden
  // and behaviour is unchanged. When present, `requiresConfirmation` gates commit (the user must confirm a
  // non-auto-applied / low-confidence layout before proceeding).
  orientation?: LabImportOrientationInfo;
}

export interface LabResultImportResponse {
  processedCount: number;
  loadedCount: number;
  erroredCount: number;
  missingCount: number;
  censoredCount: number;
  exceedanceCount: number;
  blankCorrectedCount: number;
  alertsSent: number;
  applyBlankCorrection: boolean;
  sampleMatchErrors: LabImportSampleMatchError[];
  errors: string[];
  warnings: string[];
  message: string;
  success: boolean;
}

// Validates the minimum required shape before a 400 body is accepted as a LabResultImportResponse.
// Guards against unexpected proxy/WAF/gateway 400s that merely happen to include an `errors` key.
export function isLabResultImportResponse(v: unknown): v is LabResultImportResponse {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  return Array.isArray(r['errors']) && typeof r['processedCount'] === 'number';
}

// Multipart commit payload (columns/analytes/units are serialized to JSON strings by the service).
export interface LabImportCommitPayload {
  labRequisitionId: string;
  file: File;
  applyBlankCorrection: boolean;
  columns: LabImportColumnMap;
  analytes: LabImportAnalyteMapping[];
  units: LabImportUnitMapping[];
  // DASH-2728 (AC8): the orientation the user confirmed at analyze time, echoed on commit so the BE rebuilds
  // the SAME table deterministically instead of re-running the AI structure pass (which could re-derive a
  // different layout). Mirrors LabImportCommitRequestModel.Transposed / .DataStartRowIndex (both nullable).
  // On the LEGACY override path (`headerRowIndex` omitted) `dataStartRowIndex` is a forced REAL-header-row
  // override and the BE re-detects. On the DASH-3159 applied-structure path (`headerRowIndex` supplied)
  // `dataStartRowIndex` echoes `LabImportOrientation.DataStartRowIndex` (the first DATA row) — see below.
  // All omitted for a legacy/undetected orientation, so the BE re-detects at commit exactly as before.
  transposed?: boolean;
  dataStartRowIndex?: number;
  // DASH-3159/3157: the 0-based RAW-FILE index of the real HEADER row the applied build used (echoes
  // LabImportOrientation.headerRowIndex → LabImportCommitRequestModel.HeaderRowIndex). This field is what GATES
  // the BE's deterministic applied-structure path: only when it is present does the BE rebuild from the echoed
  // transposed/dataStartRowIndex/footerRowIndices with NO AI structure call. Omitted on the legacy override
  // path (BE re-detects) and for an undetected orientation.
  headerRowIndex?: number;
  // DASH-3159: the 0-based RAW-FILE indices of the rows the BE excluded as footers in the applied build,
  // echoed on commit alongside transposed/dataStartRowIndex so the whole applied structure round-trips and the
  // BE reproduces the exact excluded-row set from the preview. Serialized to a JSON array by the service.
  // Omitted for a legacy/undetected orientation, so a BE predating the paired change ignores it.
  footerRowIndices?: number[];
  // DASH-2720: ONE optional supplemental document (typically the lab's source PDF) attached alongside the
  // results file. It is NOT parsed and does NOT affect mapping/ingestion — the BE (DASH-2719) archives it
  // on the requisition only after a successful commit. Sent as the `supplementalFile` multipart part; a BE
  // predating the paired change simply ignores the extra part, so the results-only commit is unchanged.
  supplementalFile?: File | null;
}

// Data passed into the dialog when opened for a selected requisition.
export interface LabResultImportDialogData {
  labRequisitionId: string;
  labRequisitionNumber?: string;
}
